import * as React from 'react';
import styled, { withTheme, keyframes } from 'styled-components';
import { rem } from 'polished';
import { __ } from 'react-i18n';
import { OutsideClick } from '../../../Defaults/OutsideClick';
import { Title } from '../../General/Title';
import { SvgIcon, IconType } from '../../General/SvgIcon';
import {
  FlexCol,
  FlexRowCenter,
  FlexColCenter,
} from '../../../Defaults/Flexbox/Flexbox';
import { Button } from '../../General/Button';
import { ModalItemsTable } from './ModalItemsTable';
import { MinAmountInfoText } from './MinAmountInfoText';
import {
  getZemplinProductDefaultInfo,
  getZemplinTrueCount,
  getElasticCategoryZemplinProductInfo,
} from '../../selectors';
import { ZemplinLoaderWrapper } from '../../../..';

export interface Props {
  handleClose: any;
  handleButtonClick: any;
  product: any;
  theme: any;
  isRequest: boolean;
  isLoggedIn: boolean;
  isFromCategory?: boolean;
}

function AddToCartModal({
  handleClose,
  handleButtonClick,
  product,
  theme,
  isRequest,
  isLoggedIn,
  isFromCategory,
}: Props) {
  const { productPackages } = isFromCategory
    ? getElasticCategoryZemplinProductInfo(product)
    : getZemplinProductDefaultInfo(product);
  const { unit } = isFromCategory
    ? getElasticCategoryZemplinProductInfo(product)
    : getZemplinProductDefaultInfo(product);
  const step = productPackages ? productPackages : unit === 'Mks' ? 1 : 1;
  const minAmount = unit === 'Mks' ? 0.001 : 1;
  const [isFetching, setIsFetching] = React.useState(false);
  const [count, setCount] = React.useState(step.toString());

  const handleAddToCart = () => {
    setIsFetching(true);
    handleButtonClick(getZemplinTrueCount(unit === 'Mks', parseFloat(count)));
    // setIsFetching(false);
  };

  const handleCountChange = (
    newCount: string,
    shouldAddToCart: boolean = true,
  ) => {
    const withoutCommas = newCount.replace(',', '.');
    const newCountNumber = parseFloat(withoutCommas);

    if (isNaN(newCountNumber)) {
      setCount(newCount);
      return;
    }

    if (shouldAddToCart) {
      if (parseFloat(newCount) < minAmount) {
        newCount = minAmount.toFixed(3);
      }
    }

    setCount(newCount);
  };

  return (
    <AddAccountWrapper>
      <ModalStyle />
      <ModalHolder>
        <OutsideClickWrapper>
          <OutsideClick handleFunction={handleClose}>
            <Wrapper>
              <TitleWrapper>
                <Title marginTop={0} marginBottom={0}>
                  {__('Vyberte množstvo')}
                </Title>
                <SvgIcon
                  onClick={handleClose}
                  width={24}
                  height={24}
                  icon={IconType.close}
                  alt="Zavrieť okno"
                  title="Zavrieť okno"
                  fill={theme.colors.textPrimary}
                  cursor={'pointer'}
                />
              </TitleWrapper>

              <ModalItemsTable
                count={count}
                handleCountChange={handleCountChange}
                products={[product]}
                isFetching={false}
                isLoggedIn={isLoggedIn}
                isFromCategory={isFromCategory}
              />
              {count < productPackages && <MinAmountInfoText />}
              {isFetching ? (
                <ZemplinLoaderWrapper height={120} />
              ) : (
                <AddButton
                  // tslint:disable-next-line:jsx-no-lambda
                  onClick={handleAddToCart}
                >
                  {isRequest ? __('Dopytovať') : __('Do košíka')}
                </AddButton>
              )}
            </Wrapper>
          </OutsideClick>
        </OutsideClickWrapper>
      </ModalHolder>
    </AddAccountWrapper>
  );
}

const OutsideClickWrapper = styled.div`
  width: 100%;
  max-width: ${rem(656)};
`;

const animateIn = keyframes`
   0% {
    opacity: 0;
    transform: scale(0.6) translateY(-8px);
  }
  
  100% {
    opacity: 1;
  }
`;

const Wrapper = styled(FlexCol)`
  padding: ${rem(32)};
  width: 100%;
  position: relative;
  opacity: 1;
  background: white;
  border-radius: ${rem(4)};
  margin: ${rem(16)} auto;
  box-shadow: ${({ theme }) => theme.boxShadow.default};
  display: block;
  animation: ${animateIn} 0.15s ease;

  ${({ theme }) => theme.mediab.m580`
      padding: ${rem(24)};
      margin: ${rem(16)};
      width: initial;
  `};
`;

const AddAccountWrapper = styled.div``;

const TitleWrapper = styled(FlexRowCenter)`
  justify-content: space-between;
  margin-bottom: ${rem(32)};

  ${({ theme }) => theme.mediab.m580`
      margin-bottom: ${rem(8)};
  `};
`;

const AddButton = styled(Button)`
  height: ${rem(48)};
  padding: 0 ${rem(16)};
  width: 100%;
  max-width: ${rem(160)};
  background-color: ${({ theme }) => theme.colors.secondary};
  color: ${({ theme }) => theme.colors.black};
  font-size: ${rem(14)};
  border-radius: ${rem(2)};
  margin: ${rem(32)} 0 0;

  &:disabled {
    background-color: #dcd6d6;
  }

  ${({ theme }) => theme.mediab.m580`
    max-width: initial;
    margin-top: ${rem(32)};
  `}
`;

const ModalHolder = styled(FlexColCenter)`
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: ${({ theme }) => theme.index.modal};
`;

const ModalStyle = styled.div`
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: #000000;
  opacity: 0.2;
  z-index: ${({ theme }) => theme.index.modal};
`;

const WithThemeComponent: any = withTheme(AddToCartModal);
export { WithThemeComponent as AddToCartModal };
